import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import { assetUrl, fetchJSON } from '../../utils/api';
import MerchandiseCartModal from './MerchandiseCartModal';
import MerchandiseStockRequestModal from './MerchandiseStockRequestModal';
import { addItemToCart, cartCount, loadMerchCart, saveMerchCart } from '../../utils/merchCart';

function formatNok(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n <= 0) return 'Pris kommer';
  return `${n.toLocaleString('nb-NO')} NOK`;
}

function toSafeNonNegativeInteger(value, fallback = 0) {
  const n = Number(value);
  if (!Number.isFinite(n)) return Math.max(0, Math.round(Number(fallback) || 0));
  return Math.max(0, Math.round(n));
}

function normalizeVariantStockRows(rows, fallbackStockOnHand = 0) {
  const source = Array.isArray(rows) ? rows : [];
  if (!source.length) {
    return [{ stockOnHand: toSafeNonNegativeInteger(fallbackStockOnHand), reservedCount: 0 }];
  }
  return source.map((row) => ({
    stockOnHand: toSafeNonNegativeInteger(row?.stockOnHand),
    reservedCount: toSafeNonNegativeInteger(row?.reservedCount),
  }));
}

function deriveAvailableStock(entry) {
  const direct = Number(entry?.totalAvailableStock);
  if (Number.isFinite(direct)) return Math.max(0, Math.round(direct));

  const rows = normalizeVariantStockRows(entry?.variantStock, entry?.stockOnHand);
  const sum = rows.reduce(
    (total, row) => total + Math.max(0, toSafeNonNegativeInteger(row.stockOnHand) - toSafeNonNegativeInteger(row.reservedCount)),
    0,
  );
  if (sum > 0) return sum;

  const totalStock = Number(entry?.totalStockOnHand ?? entry?.stockOnHand);
  const totalReserved = Number(entry?.totalReservedCount || 0);
  if (Number.isFinite(totalStock) || Number.isFinite(totalReserved)) {
    return Math.max(0, Math.round((Number(totalStock) || 0) - (Number(totalReserved) || 0)));
  }

  return 0;
}

function normalizeVariantGroups(input) {
  if (!Array.isArray(input)) return [];
  return input
    .map((group) => ({
      title: String(group?.title || '').trim(),
      options: Array.isArray(group?.options) ? group.options.map((value) => String(value || '').trim()).filter(Boolean) : [],
    }))
    .filter((group) => group.title && group.options.length);
}

function normalizeVariantAttributes(input) {
  const source = Array.isArray(input) ? input : [];
  const seen = new Set();
  const normalized = [];
  source.forEach((entry) => {
    const title = String(entry?.title || '').trim().slice(0, 120);
    const value = String(entry?.value || '').trim().slice(0, 120);
    if (!title || !value) return;
    const key = `${title.toLowerCase()}::${value.toLowerCase()}`;
    if (seen.has(key)) return;
    seen.add(key);
    normalized.push({ title, value });
  });
  return normalized;
}

function normalizeVariantStockRowsDetailed(rows, fallbackStockOnHand = 0) {
  const source = Array.isArray(rows) ? rows : [];
  if (!source.length) {
    return [{
      key: 'base',
      label: 'Standard',
      attributes: [],
      stockOnHand: toSafeNonNegativeInteger(fallbackStockOnHand),
      reservedCount: 0,
    }];
  }

  return source.map((row) => ({
    key: String(row?.key || '').trim() || 'base',
    label: String(row?.label || '').trim() || 'Standard',
    attributes: normalizeVariantAttributes(row?.attributes),
    stockOnHand: toSafeNonNegativeInteger(row?.stockOnHand),
    reservedCount: toSafeNonNegativeInteger(row?.reservedCount),
  }));
}

function variantRowAvailableStock(row) {
  return Math.max(0, toSafeNonNegativeInteger(row?.stockOnHand) - toSafeNonNegativeInteger(row?.reservedCount));
}

function rowMatchesSelection(row, selectionRows) {
  const map = new Map(normalizeVariantAttributes(row?.attributes).map((entry) => [String(entry.title || '').trim().toLowerCase(), String(entry.value || '').trim()]));
  return (Array.isArray(selectionRows) ? selectionRows : []).every((selection) => {
    const title = String(selection?.title || '').trim();
    const value = String(selection?.value || '').trim();
    if (!title || !value) return false;
    return String(map.get(title.toLowerCase()) || '') === value;
  });
}

function deriveRequestedSizeFromVariants(selectedVariants) {
  const rows = Array.isArray(selectedVariants) ? selectedVariants : [];
  const sizeRow = rows.find((entry) => /size|stoerrelse|størrelse/i.test(String(entry?.title || '')));
  return sizeRow ? String(sizeRow.value || '').trim().toUpperCase() : '';
}

function buildInventoryTableData(variantGroups, variantStock) {
  const groups = normalizeVariantGroups(variantGroups);
  const rows = Array.isArray(variantStock) ? variantStock : [];
  const firstGroup = groups[0] || null;
  const secondGroup = groups[1] || null;

  if (!firstGroup) {
    return {
      rowHeader: 'Variant',
      columnLabels: ['Stock'],
      rowLabels: ['Standard'],
      lookup: { 'Standard::Stock': rows.reduce((sum, row) => sum + variantRowAvailableStock(row), 0) },
    };
  }

  const rowLabels = Array.isArray(firstGroup.options)
    ? firstGroup.options.map((value) => String(value || '').trim()).filter(Boolean)
    : [];

  if (!secondGroup) {
    const lookup = {};
    rowLabels.forEach((rowLabel) => {
      const stockRow = rows.find((row) => rowMatchesSelection(row, [{ title: firstGroup.title, value: rowLabel }]));
      lookup[`${rowLabel}::Stock`] = stockRow ? variantRowAvailableStock(stockRow) : 0;
    });
    return {
      rowHeader: String(firstGroup.title || 'Variant'),
      columnLabels: ['Stock'],
      rowLabels,
      lookup,
    };
  }

  const columnLabels = Array.isArray(secondGroup.options)
    ? secondGroup.options.map((value) => String(value || '').trim()).filter(Boolean)
    : [];
  const lookup = {};
  rowLabels.forEach((rowLabel) => {
    columnLabels.forEach((columnLabel) => {
      const stockRow = rows.find((row) => rowMatchesSelection(row, [
        { title: firstGroup.title, value: rowLabel },
        { title: secondGroup.title, value: columnLabel },
      ]));
      lookup[`${rowLabel}::${columnLabel}`] = stockRow ? variantRowAvailableStock(stockRow) : 0;
    });
  });

  return {
    rowHeader: String(firstGroup.title || 'Variant'),
    columnLabels,
    rowLabels,
    lookup,
  };
}

function buildSelectedVariantsForInventoryCell(variantGroups, rowLabel, columnLabel) {
  const groups = normalizeVariantGroups(variantGroups);
  if (!groups.length) return [];

  const selectedVariants = [];
  if (rowLabel) {
    selectedVariants.push({ title: groups[0].title, value: String(rowLabel || '').trim() });
  }
  if (columnLabel && groups[1]) {
    selectedVariants.push({ title: groups[1].title, value: String(columnLabel || '').trim() });
  }
  return selectedVariants.filter((entry) => entry.title && entry.value);
}

function getInventoryActionState(stockCount) {
  if (stockCount > 10) {
    return {
      action: 'cart',
      label: 'ADD TO CART',
      borderColor: '#83ffc3',
      textColor: '#effff5',
      background: 'rgba(10, 54, 27, 0.82)',
    };
  }
  if (stockCount > 0) {
    return {
      action: 'cart',
      label: 'ADD TO CART',
      borderColor: '#ffd667',
      textColor: '#fff6d6',
      background: 'rgba(84, 63, 10, 0.82)',
    };
  }
  return {
    action: 'request',
    label: 'OUT OF STOCK',
    borderColor: '#ff9999',
    textColor: '#ffe2e2',
    background: 'rgba(78, 21, 21, 0.82)',
  };
}

const EMPTY_STOCK_REQUEST_DRAFT = {
  open: false,
  entry: null,
  selectedVariants: [],
  requestKey: '',
};

const MerchandisePicker3D = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [shippingCostNok, setShippingCostNok] = useState(75);
  const [shippingCostInternationalNok, setShippingCostInternationalNok] = useState(150);
  const [cartItems, setCartItems] = useState(() => loadMerchCart());
  const [showCart, setShowCart] = useState(false);
  const [buyEntry, setBuyEntry] = useState(null);
  const [cartFeedback, setCartFeedback] = useState('');
  const [inventoryActionPendingKey, setInventoryActionPendingKey] = useState('');
  const [stockRequestDraft, setStockRequestDraft] = useState(EMPTY_STOCK_REQUEST_DRAFT);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchJSON('/api/merchandise/products/public', { cache: 'no-store' });
        const products = (Array.isArray(res?.items) ? res.items : []).filter((entry) => Number(entry?.salePriceNok || 0) > 0);
        const shipping = Number(res?.shippingCostNok);
        const shippingInternational = Number(res?.shippingCostInternationalNok);
        if (active && Number.isFinite(shipping) && shipping >= 0) {
          setShippingCostNok(Math.round(shipping));
        }
        if (active && Number.isFinite(shippingInternational) && shippingInternational >= 0) {
          setShippingCostInternationalNok(Math.round(shippingInternational));
        }
        if (active) setEntries(products);
      } catch (e) {
        if (active) setError(String(e?.message || 'Could not load merchandise products'));
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    saveMerchCart(cartItems);
  }, [cartItems]);

  useEffect(() => {
    if (!cartFeedback) return;
    const timer = setTimeout(() => setCartFeedback(''), 1800);
    return () => clearTimeout(timer);
  }, [cartFeedback]);

  const addEntryToCart = (entry, selectedMap = {}) => {
    if (!entry?._id) return;
    const variantGroups = normalizeVariantGroups(entry.variantGroups);
    const selectedVariants = variantGroups
      .map((group) => ({ title: group.title, value: String(selectedMap[group.title] || '').trim() }))
      .filter((row) => row.title && row.value);
    const requestedSize = deriveRequestedSizeFromVariants(selectedVariants);

    const next = addItemToCart(cartItems, {
      productId: String(entry._id || '').trim(),
      itemTitle: String(entry.title || 'Merchandise').trim(),
      itemImageSrc: entry.thumbUrl || '',
      saleType: requestedSize ? 'T-shirt' : 'Other',
      salePriceNok: Number(entry.salePriceNok || 0) || 0,
      requestedSize,
      selectedVariants,
      variantGroups,
      quantity: 1,
    });

    setCartItems(next);
    saveMerchCart(next);
    setBuyEntry(null);
    setShowCart(true);
    setCartFeedback('Added to DØDSEGO CART');
  };

  const openBuyFlow = (entry) => {
    setBuyEntry(entry);
  };

  const openStockRequestModal = (entry, selectedVariants, requestKey) => {
    const nextKey = String(requestKey || 'inventory-request').trim() || 'inventory-request';
    setStockRequestDraft({
      open: true,
      entry: entry || null,
      selectedVariants: Array.isArray(selectedVariants) ? selectedVariants : [],
      requestKey: nextKey,
    });
  };

  const requestVariantStock = async (entry, selectedVariants, requestKey, requestDetails = {}) => {
    if (!entry?._id) return false;
    const nextKey = String(requestKey || 'inventory-request').trim() || 'inventory-request';
    setInventoryActionPendingKey(nextKey);
    try {
      await fetchJSON(`/api/merchandise/products/${encodeURIComponent(String(entry._id || ''))}/public-stock-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          selectedVariants: Array.isArray(selectedVariants) ? selectedVariants : [],
          requestedSize: deriveRequestedSizeFromVariants(selectedVariants),
          customerName: String(requestDetails?.customerName || '').trim(),
          customerEmail: String(requestDetails?.customerEmail || '').trim(),
          requestText: String(requestDetails?.requestText || '').trim(),
        }),
      });
      setCartFeedback('Stock request sent');
      return true;
    } catch (requestError) {
      const text = String(requestError?.message || '');
      if (text.includes('stock_available')) {
        setCartFeedback('This combination is back in stock. Add it to cart.');
      } else if (text.includes('no_assignees')) {
        setCartFeedback('No assignee is configured for stock requests.');
      } else if (
        text.includes('invalid_customer_name')
        || text.includes('invalid_customer_email')
        || text.includes('invalid_request_text')
      ) {
        setCartFeedback('Please fill in all request fields.');
      } else {
        setCartFeedback('Could not send stock request.');
      }
      return false;
    } finally {
      setInventoryActionPendingKey('');
    }
  };

  const submitStockRequestFromModal = async ({ customerName, customerEmail, requestText }) => {
    const entry = stockRequestDraft?.entry;
    if (!entry?._id) {
      return { ok: false, message: 'Could not resolve product.' };
    }

    const ok = await requestVariantStock(
      entry,
      stockRequestDraft.selectedVariants,
      stockRequestDraft.requestKey,
      { customerName, customerEmail, requestText }
    );

    if (ok) {
      setStockRequestDraft(EMPTY_STOCK_REQUEST_DRAFT);
      setBuyEntry(null);
    }

    return ok
      ? { ok: true }
      : { ok: false, message: 'Could not send stock request.' };
  };

  const totalItems = useMemo(() => cartCount(cartItems), [cartItems]);
  const buyEntryVariantGroups = useMemo(() => normalizeVariantGroups(buyEntry?.variantGroups), [buyEntry]);
  const buyEntryVariantStockRows = useMemo(
    () => normalizeVariantStockRowsDetailed(buyEntry?.variantStock, buyEntry?.stockOnHand),
    [buyEntry]
  );
  const buyEntryInventoryTable = useMemo(
    () => buildInventoryTableData(buyEntryVariantGroups, buyEntryVariantStockRows),
    [buyEntryVariantGroups, buyEntryVariantStockRows]
  );
  const mobileButtonStyle = isMobile
    ? {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 1.2,
      boxSizing: 'border-box',
      textAlign: 'center',
    }
    : {};

  return (
    <div style={{ minHeight: '100vh', background: 'radial-gradient(1200px 700px at 10% -20%, #24323f 0%, #07090d 65%), linear-gradient(150deg, #091318 0%, #0d0908 100%)', color: '#fff', padding: '116px clamp(10px, 3.4vw, 22px) 28px', textAlign: 'left' }}>
      <div style={{ width: 'min(1200px, 100%)', margin: '0 auto', marginBottom: isMobile ? 50 : 0, display: 'grid', gap: 20, textAlign: 'left' }}>
        <div style={{ display: 'grid', gap: 6, marginTop: 12, justifyItems: 'start', textAlign: 'left' }}>
          <div style={{ textTransform: 'uppercase', letterSpacing: '0.12em', opacity: 0.7, fontSize: 12 }}>DØDSEGO</div>
          <h1 style={{ margin: 0, fontSize: 'clamp(28px, 6vw, 58px)', lineHeight: 0.95 }}>MERCHANDISE</h1>
        </div>

        {error ? <div style={{ border: '1px solid rgba(255,130,130,0.45)', borderRadius: 10, padding: 12, background: 'rgba(80,0,0,0.24)' }}>{error}</div> : null}
        {loading ? <div style={{ opacity: 0.9 }}>Laster merchandise...</div> : null}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(340px, 100%), 1fr))', gap: 16 }}>
          {entries.map((entry) => {
            const availableStock = deriveAvailableStock(entry);
            const hasStock = availableStock > 0;
            return (
              <article key={entry._id} style={{ borderRadius: 14, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.22)', background: 'linear-gradient(180deg, rgba(30,35,40,0.7), rgba(8,8,9,0.85))', display: 'grid', gridTemplateRows: 'auto 1fr', minWidth: 0 }}>
                <Link to={`/merchandise/${encodeURIComponent(String(entry._id || ''))}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div style={{ background: '#0f0f10', position: 'relative', aspectRatio: '4/3', overflow: 'hidden' }}>
                    {entry.thumbUrl ? (
                      <img src={assetUrl(entry.thumbUrl)} alt={entry.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : null}
                  </div>
                </Link>
                <div style={{ padding: 12, display: 'grid', gap: 7 }}>
                  <div style={{ fontWeight: 700, fontSize: 20, textAlign: 'center' }}>{entry.title}</div>
                  <div style={{ opacity: 0.95, fontSize: 14.6, textAlign: 'center', lineHeight: 1.3 }}>
                    {String(entry.blurb || '').trim() || entry.description || ''}
                  </div>
                  <div style={{ opacity: 0.9, fontSize: 13, textAlign: 'center' }}>{formatNok(entry.salePriceNok)}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', color: hasStock ? '#83ffc3' : '#ffd4d4', textAlign: 'center' }}>
                    {hasStock ? 'IN STOCK' : 'OUT OF STOCK, LONG DELIVERY TIME'}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
                    <Link to={`/merchandise/${encodeURIComponent(String(entry._id || ''))}`} style={{ textDecoration: 'none' }}>
                      <button type='button' style={{ ...mobileButtonStyle, borderRadius: 999, border: '1px solid #fff', background: '#000', color: '#fff', padding: isMobile ? '12px 14px' : '8px 14px', cursor: 'pointer' }}>INFORMATION</button>
                    </Link>
                    <button type='button' onClick={() => openBuyFlow(entry)} style={{ ...mobileButtonStyle, borderRadius: 999, border: '1px solid #63f0a0', background: 'rgba(6,70,30,0.45)', color: '#d7ffe9', padding: isMobile ? '12px 14px' : '8px 14px', cursor: 'pointer' }}>PURCHASE</button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>

      <button
        type='button'
        onClick={() => setShowCart(true)}
        style={{ ...mobileButtonStyle, position: 'fixed', right: 18, bottom: 18, zIndex: 1200, borderRadius: 999, border: '1px solid #fff', background: '#0a0a0a', color: '#fff', padding: isMobile ? '15px 16px' : '10px 16px', cursor: 'pointer', boxShadow: '0 10px 30px rgba(0,0,0,0.45)' }}
      >
        DØDSEGO CART ({totalItems})
      </button>

      {cartFeedback ? (
        <div style={{ position: 'fixed', left: '50%', bottom: 24, transform: 'translateX(-50%)', zIndex: 1250, borderRadius: 999, border: '1px solid rgba(108,255,159,0.7)', background: 'rgba(6,58,24,0.88)', color: '#e4ffe8', padding: '8px 14px' }}>
          {cartFeedback}
        </div>
      ) : null}

      {buyEntry ? (
        <div
          onClick={() => setBuyEntry(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 1240, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: 'min(760px, 94vw)', borderRadius: 14, border: '1px solid rgba(255,255,255,0.26)', background: 'linear-gradient(135deg, rgba(30,35,40,0.94), rgba(8,8,9,0.96))', color: '#fff', padding: 18, display: 'grid', gap: 14 }}
          >
            <div style={{ display: 'grid', gap: 6 }}>
              <div style={{ textTransform: 'uppercase', letterSpacing: '0.12em', fontSize: 12, opacity: 0.82, color: '#f8fbff', textAlign: 'center' }}>
                INVENTORY STATUS
              </div>
              <h3 style={{ margin: 0, textAlign: 'center', fontSize: 26 }}>{buyEntry.title || 'Merchandise'}</h3>
              <div style={{ textAlign: 'center', opacity: 0.9 }}>{formatNok(buyEntry.salePriceNok)}</div>
              {String(buyEntry.blurb || buyEntry.description || '').trim() ? (
                <div style={{ textAlign: 'center', opacity: 0.88, lineHeight: 1.4 }}>{String(buyEntry.blurb || buyEntry.description || '').trim()}</div>
              ) : null}
            </div>

            {!buyEntryVariantGroups.length ? (
              (() => {
                const totalStock = deriveAvailableStock(buyEntry);
                const actionState = getInventoryActionState(totalStock);
                const requestKey = `inventory::${String(buyEntry._id || '')}::standard`;
                const isPending = inventoryActionPendingKey === requestKey;
                return (
                  <div style={{ display: 'grid', justifyItems: 'center', gap: 10 }}>
                    <button
                      type='button'
                      disabled={isPending}
                      onClick={() => {
                        if (actionState.action === 'request') {
                          openStockRequestModal(buyEntry, [], requestKey);
                          return;
                        }
                        addEntryToCart(buyEntry, {});
                      }}
                      style={{ ...mobileButtonStyle, minWidth: 220, borderRadius: 10, border: `2px solid ${actionState.borderColor}`, background: actionState.background, color: actionState.textColor, padding: isMobile ? '18px 18px' : '12px 18px', fontWeight: 700, letterSpacing: '0.08em', cursor: isPending ? 'wait' : 'pointer', opacity: isPending ? 0.72 : 0.96 }}
                    >
                      {isPending ? 'SENDING REQUEST...' : actionState.label}
                    </button>
                  </div>
                );
              })()
            ) : (
              <div style={{ overflowX: 'auto', display: 'flex', justifyContent: 'center' }}>
                <table style={{ width: 'auto', maxWidth: '100%', borderCollapse: 'collapse', tableLayout: 'auto', margin: '0 auto' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', fontSize: 12, opacity: 0.85, borderBottom: '1px solid rgba(255,255,255,0.2)', padding: '8px 10px' }}>
                        {buyEntryInventoryTable.rowHeader}
                      </th>
                      {buyEntryInventoryTable.columnLabels.map((columnLabel) => (
                        <th key={columnLabel} style={{ textAlign: 'center', fontSize: 12, opacity: 0.85, borderBottom: '1px solid rgba(255,255,255,0.2)', padding: '8px 10px' }}>
                          {columnLabel}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {buyEntryInventoryTable.rowLabels.map((rowLabel) => (
                      <tr key={rowLabel}>
                        <td style={{ textAlign: 'left', fontSize: 13, borderBottom: '1px solid rgba(255,255,255,0.1)', padding: '8px 10px' }}>{rowLabel}</td>
                        {buyEntryInventoryTable.columnLabels.map((columnLabel) => {
                          const stock = Number(buyEntryInventoryTable.lookup[`${rowLabel}::${columnLabel}`] || 0);
                          const actionState = getInventoryActionState(stock);
                          const selectedVariants = buildSelectedVariantsForInventoryCell(buyEntryVariantGroups, rowLabel, buyEntryInventoryTable.columnLabels.length > 1 ? columnLabel : '');
                          const requestKey = `${String(buyEntry._id || '')}::${rowLabel}::${columnLabel}`;
                          const isPending = inventoryActionPendingKey === requestKey;
                          const selectedMap = Object.fromEntries(selectedVariants.map((entry) => [entry.title, entry.value]));
                          return (
                            <td key={`${rowLabel}-${columnLabel}`} style={{ textAlign: 'center', borderBottom: '1px solid rgba(255,255,255,0.1)', padding: '8px 10px' }}>
                              <button
                                type='button'
                                disabled={isPending}
                                onClick={() => {
                                  if (actionState.action === 'request') {
                                    openStockRequestModal(buyEntry, selectedVariants, requestKey);
                                    return;
                                  }
                                  addEntryToCart(buyEntry, selectedMap);
                                }}
                                style={{ ...mobileButtonStyle, width: '100%', minWidth: 138, padding: isMobile ? '14px 10px' : '9px 10px', borderRadius: 9, border: `2px solid ${actionState.borderColor}`, background: actionState.background, color: actionState.textColor, fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', cursor: isPending ? 'wait' : 'pointer', opacity: isPending ? 0.72 : 0.96 }}
                              >
                                {isPending ? 'SENDING REQUEST...' : actionState.label}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-start', gap: 8 }}>
              <button
                type='button'
                onClick={() => setBuyEntry(null)}
                style={{ ...mobileButtonStyle, borderRadius: 8, border: '1px solid #444', background: '#111', color: '#fff', padding: isMobile ? '12px 11px' : '8px 11px', cursor: 'pointer' }}
              >
                CLOSE
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <MerchandiseStockRequestModal
        open={stockRequestDraft.open}
        pending={stockRequestDraft.open && inventoryActionPendingKey === stockRequestDraft.requestKey}
        productTitle={stockRequestDraft.entry?.title || ''}
        selectedVariants={stockRequestDraft.selectedVariants}
        onClose={() => setStockRequestDraft(EMPTY_STOCK_REQUEST_DRAFT)}
        onSubmit={submitStockRequestFromModal}
      />

      <MerchandiseCartModal
        open={showCart}
        cartItems={cartItems}
        setCartItems={setCartItems}
        shippingCostNok={shippingCostNok}
        shippingCostInternationalNok={shippingCostInternationalNok}
        onClose={() => setShowCart(false)}
        onOrderCompleted={() => navigate('/')}
      />
    </div>
  );
};

export default MerchandisePicker3D;
